
import { Component, ChangeDetectionStrategy, inject, signal, OnInit, computed } from '@angular/core';
import { RouterLink } from '@angular/router';
import { BillingService } from '../../services/billing.service';
import { AuthService } from '../../services/auth.service';
import { Kpi, UsageStats, Subscription, Plan } from '../../models/index';
import { KpiCardComponent } from '../kpi-card/kpi-card.component';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { CommonModule } from '@angular/common';

type LoadingState = 'loading' | 'loaded' | 'error';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  templateUrl: './dashboard.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [KpiCardComponent, TranslatePipe, RouterLink, CommonModule],
})
export class DashboardComponent implements OnInit {
  private billingService = inject(BillingService);
  private authService = inject(AuthService);

  kpis = signal<Kpi[]>([]);
  usage = signal<UsageStats | null>(null);
  subscription = signal<Subscription | null>(null);
  loadingState = signal<LoadingState>('loading');

  // Helpers for Status
  isTrialStatus = computed(() => this.subscription()?.status === 'trialing');
  
  // คำนวณวันที่เหลือ
  trialDaysLeft = computed(() => {
    const end = this.subscription()?.current_period_end;
    if (!end) return 0;
    const diff = new Date(end).getTime() - new Date().getTime();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  });

  // Logic: แสดงแบนเนอร์สีน้ำเงินเมื่อเป็น Trial และยังมีวันเหลือ
  showTrialBanner = computed(() => {
    return this.isTrialStatus() && this.trialDaysLeft() > 0;
  });

  // Logic: แสดงแบนเนอร์สีแดงเมื่อ Trial หมดวัน หรือ สถานะเป็น Past Due / Canceled / Unpaid
  showExpiredBanner = computed(() => {
    const sub = this.subscription();
    if (!sub) return false;
    
    // Case 1: Trial expired (days <= 0)
    if (sub.status === 'trialing' && this.trialDaysLeft() <= 0) return true;
    
    // Case 2: Hard status (Past Due, Canceled, Unpaid)
    if (['past_due', 'canceled', 'unpaid'].includes(sub.status)) return true;

    return false;
  });

  currentPlanName = computed(() => {
    const sub = this.subscription();
    if (sub?.plan) return sub.plan.name;
    
    // Fallback logic
    const storePlanId = this.authService.currentStore()?.plan_id;
    if (storePlanId === 'tier_l') return 'Tier L (Enterprise)';
    if (storePlanId === 'tier_m') return 'Tier M (Growth)';
    if (storePlanId === 'tier_s') return 'Tier S (Starter)';
    
    return 'Starter';
  });

  usageProgress = computed(() => {
    const stats = this.usage();
    const sub = this.subscription();
    const limit = sub?.plan?.limits?.medical_cases || 500; 
    if (!stats) return 0;
    return Math.min(100, (stats.medical_cases_used / limit) * 100);
  });

  ngOnInit(): void {
    this.loadData();
  }

  async loadData(): Promise<void> {
    this.loadingState.set('loading');
    try {
      const [usageStats, subData] = await Promise.all([
        this.billingService.getUsageStats(),
        this.billingService.getSubscription()
      ]);

      this.usage.set(usageStats);
      this.subscription.set(subData);

      // FIX: Calculate days remaining safely
      let daysUntilReset = 0;
      if (usageStats && usageStats.next_reset_date) {
          const resetDate = new Date(usageStats.next_reset_date);
          const today = new Date();
          const diffTime = resetDate.getTime() - today.getTime();
          daysUntilReset = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          // Prevent negative display if date passed slightly before refresh
          if (daysUntilReset < 0) daysUntilReset = 0;
      }

      const kpis: Kpi[] = [
          { title: 'Total Cases (Current Cycle)', value: usageStats?.medical_cases_used || 0, changeValue: 0, changePercent: 0, prefix: '' },
          { title: 'Active Staff', value: usageStats?.users_active || 0, changeValue: 0, changePercent: 0, prefix: '' },
          { 
            title: 'Days until Reset', 
            value: daysUntilReset, 
            changeValue: 0, 
            changePercent: 0, 
            prefix: '' 
          },
          { title: 'System Status', value: 1, changeValue: 0, changePercent: 0, prefix: 'OK' }
      ];
      
      this.kpis.set(kpis);
      this.loadingState.set('loaded');
    } catch (e) {
      console.error('Failed to load dashboard:', e);
      this.loadingState.set('error');
    }
  }
}
